import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Label,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Tooltip,
} from "@fluentui/react-components";
import { VirtualizerScrollView } from "@fluentui-contrib/react-virtualizer";
import { ArrowSyncCircleRegular, SearchRegular } from "@fluentui/react-icons";
import { ConstantValues } from "common/constants";
import { saveAs } from "file-saver";
import {
  DragEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import _Plot from "react-plotly.js";
const Plot = (_Plot as any).default ?? _Plot;
import { useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import { showToast } from "../../redux/toastActions";
import {
  getProjectImpactAnalysis,
  getProjectReviews,
  mergeTopics,
  mergeTopicsInProject,
  renameTopic,
  renameTopicInProject,
  updateProjectImpactAnalysis,
} from "./decoodaapi";
import { designTokens } from "theme/customTheme";
import { useDecoodaReviewsStyles } from "./DecoodaReviews.styles";
import {
  IATDProps,
  IChartDataPoint,
  IDragMergeInfo,
  IHighlightedCellProps,
  IImpactAnalysisData,
  IImpactAnalysisProps,
  IImpactAnalysisResponse,
  IImpactScoreItem,
  IReview,
  ITextSegment,
  ITopicInfo,
} from "./DecoodaReviews.types";
import { formatExportFilename } from "./components/projects/projectUtils";

const ATD_PAGE_SIZE = 10;

const HighlightedCell = ({
  text,
  highlightRanges,
  highlightClass,
  maxChars,
  cellClassName,
}: IHighlightedCellProps): JSX.Element => {
  const styles = useDecoodaReviewsStyles();

  const isTruncated = maxChars !== undefined && text.length > maxChars;
  const displayText = isTruncated ? text.slice(0, maxChars) + "…" : text;

  const cell = (
    <TableCell className={cellClassName}>
      <TableCellLayout>{displayText}</TableCellLayout>
    </TableCell>
  );

  if (isTruncated) {
    return (
      <Tooltip content={text} relationship="description">
        {cell}
      </Tooltip>
    );
  }

  if (!highlightRanges || highlightRanges.length === 0) {
    return cell;
  }

  const ranges = highlightRanges
    .split(";")
    .map((r) => r.split("-").map(Number))
    .sort((a, b) => a[0] - b[0]);

  const mergedRanges: number[][] = [];
  for (const [start, end] of ranges) {
    if (mergedRanges.length === 0) {
      mergedRanges.push([start, end]);
    } else {
      const last = mergedRanges[mergedRanges.length - 1];
      if (start <= last[1] + 1) {
        last[1] = Math.max(last[1], end);
      } else {
        mergedRanges.push([start, end]);
      }
    }
  }

  const segments: ITextSegment[] = [];
  let lastIndex = 0;

  for (const [start, end] of mergedRanges) {
    if (lastIndex < start) {
      segments.push({
        text: text.slice(lastIndex, start),
        highlight: false,
      });
    }
    segments.push({
      text: text.slice(start, end + 1),
      highlight: true,
    });
    lastIndex = end + 1;
  }
  if (lastIndex < text.length) {
    segments.push({
      text: text.slice(lastIndex),
      highlight: false,
    });
  }

  return (
    <TableCell className={cellClassName}>
      <TableCellLayout>
        {segments.map((seg, index) => (
          <span
            key={index}
            className={
              seg.highlight
                ? (highlightClass ?? styles.highlightedText)
                : styles.normalText
            }
          >
            {seg.text}
          </span>
        ))}
      </TableCellLayout>
    </TableCell>
  );
};

export const ImpactAnalysis = ({
  projectId,
  projectName,
  startDownload,
  setStartDownload,
}: IImpactAnalysisProps): JSX.Element => {
  const styles = useDecoodaReviewsStyles();
  const [impactThreshold, setImpactThreshold] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [currentThreshold, setCurrentThreshold] = useState<number | null>(null);

  const [impactAnalysisPositive, setImpactAnalysisPositive] = useState<
    IChartDataPoint[]
  >([]);
  const [impactAnalysisNegative, setImpactAnalysisNegative] = useState<
    IChartDataPoint[]
  >([]);
  const [topicsPositiveChartData, setTopicsPositiveChartData] = useState<
    IChartDataPoint[]
  >([]);
  const [topicsNegativeChartData, setTopicsNegativeChartData] = useState<
    IChartDataPoint[]
  >([]);

  const [hasImpactData, setHasImpactData] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (startDownload) {
      downloadImpactAnalysis().finally(() => setStartDownload(false));
    }
  }, [startDownload, setStartDownload]);

  const filterTopicsForBarChart = (
    scores: IImpactScoreItem[],
    allImpactAnalysisData: Record<string, IImpactAnalysisData>,
  ): IChartDataPoint[] => {
    const existingTopicsForThisThreshold = scores.map((d) => d.name);

    return Object.values(allImpactAnalysisData)
      .filter((t) => existingTopicsForThisThreshold.includes(t.topicName))
      .map((t) => ({
        x: t.totalTopicCount,
        y: t.topicName,
      }))
      .sort((a, b) => (a.x as number) - (b.x as number));
  };

  const processImpactData = (data: IImpactAnalysisResponse): void => {
    setCurrentThreshold(data.threshold);
    setHasImpactData(Object.keys(data.impactAnalysisData || {}).length > 0);

    setImpactAnalysisPositive(
      data.positiveImpactScore.map((d) => ({
        x: d.score,
        y: d.frequency,
        name: d.name,
      })),
    );

    setTopicsPositiveChartData(
      filterTopicsForBarChart(
        data.positiveImpactScore,
        data.impactAnalysisData,
      ),
    );

    setTopicsNegativeChartData(
      filterTopicsForBarChart(
        data.negativeImpactScore,
        data.impactAnalysisData,
      ),
    );

    setImpactAnalysisNegative(
      data.negativeImpactScore.map((d) => ({
        x: d.score,
        y: d.frequency,
        name: d.name,
      })),
    );
  };

  const fetchImpactAnalysis = useCallback(async (): Promise<void> => {
    if (!projectId) return;

    try {
      setIsLoading(true);
      setError(null);
      const data = await getProjectImpactAnalysis({ projectId });
      processImpactData(data);
    } catch (err) {
      console.error("Error fetching impact analysis:", err);
      setError("Failed to load impact analysis data");
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchImpactAnalysis();
  }, [fetchImpactAnalysis]);

  const handleThresholdChange = async (): Promise<void> => {
    if (!impactThreshold || !projectId) return;

    try {
      setIsUpdating(true);
      const data = await updateProjectImpactAnalysis({
        projectId,
        threshold: Number(impactThreshold),
        dryRun: false,
      });
      processImpactData(data);
      setImpactThreshold(ConstantValues.EMPTY_STRING);
    } catch (err) {
      console.error("Error updating threshold:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  const downloadImpactAnalysis = async (): Promise<void> => {
    if (!projectId) return;

    const resultsIAPositive: (string | number | null)[][] = [];
    const resultsIANegative: (string | number | null)[][] = [];
    const frequencyIAPositive: (string | number)[][] = [];
    const frequencyIANegative: (string | number)[][] = [];

    const thresholds = [0, 5, 10, 15, 20];
    const results = await Promise.allSettled(
      thresholds.map((threshold) =>
        updateProjectImpactAnalysis({
          projectId,
          threshold,
          dryRun: true,
        }).then((ia) => ({ ia, threshold })),
      ),
    );

    for (const result of results) {
      if (result.status === "rejected") {
        console.error("Error getting data for threshold:", result.reason);
        continue;
      }
      const { ia, threshold } = result.value;

      for (const score of ia.positiveImpactScore) {
        resultsIAPositive.push([
          score.name,
          score.frequency,
          score.score,
          threshold,
        ]);
      }
      for (const score of ia.negativeImpactScore) {
        resultsIANegative.push([
          score.name,
          score.frequency,
          score.score,
          threshold,
        ]);
      }

      for (const freq of filterTopicsForBarChart(
        ia.positiveImpactScore,
        ia.impactAnalysisData,
      )) {
        frequencyIAPositive.push([
          freq.y as string,
          freq.x as number,
          threshold,
        ]);
      }

      for (const freq of filterTopicsForBarChart(
        ia.negativeImpactScore,
        ia.impactAnalysisData,
      )) {
        frequencyIANegative.push([
          freq.y as string,
          freq.x as number,
          threshold,
        ]);
      }
    }

    const workbook = XLSX.utils.book_new();
    const worksheet1 = XLSX.utils.aoa_to_sheet([
      ["Topic", "Frequency", "Impact", "Threshold"],
      ...resultsIAPositive,
    ]);
    const worksheet2 = XLSX.utils.aoa_to_sheet([
      ["Topic", "Frequency", "Impact", "Threshold"],
      ...resultsIANegative,
    ]);
    const worksheet3 = XLSX.utils.aoa_to_sheet([
      ["Topic", "Frequency", "Threshold"],
      ...frequencyIAPositive,
    ]);
    const worksheet4 = XLSX.utils.aoa_to_sheet([
      ["Topic", "Frequency", "Threshold"],
      ...frequencyIANegative,
    ]);

    XLSX.utils.book_append_sheet(workbook, worksheet1, "Pos_AI");
    XLSX.utils.book_append_sheet(workbook, worksheet3, "Pos_Freq");
    XLSX.utils.book_append_sheet(workbook, worksheet2, "Neg_AI");
    XLSX.utils.book_append_sheet(workbook, worksheet4, "Neg_Freq");

    const wbOut = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbOut], { type: "application/octet-stream" });
    saveAs(
      blob,
      formatExportFilename(projectName, "ImpactAnalysisResults", "xlsx"),
    );
  };

  if (isLoading) {
    return (
      <div className={styles.centeredContainer}>
        <Spinner label="Loading impact analysis..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.centeredColumnContainer}>
        <Text>{error}</Text>
        <Button appearance="primary" onClick={fetchImpactAnalysis}>
          Retry
        </Button>
      </div>
    );
  }

  if (!hasImpactData) {
    return (
      <div className={styles.centeredColumnContainerSmallGap}>
        <Text size={400}>
          No impact analysis data available for this project.
        </Text>
        <Text size={300} className={styles.fadedText}>
          Score the project to generate impact analysis data.
        </Text>
      </div>
    );
  }

  const hasScores =
    impactAnalysisPositive.length > 0 || impactAnalysisNegative.length > 0;

  return (
    <div className={styles.impactAnalysisContainer}>
      <div className={styles.thresholdInputRow}>
        <Input
          placeholder="Threshold"
          type="number"
          min={0}
          max={100}
          value={impactThreshold}
          onChange={(_, data) => setImpactThreshold(data.value)}
          className={styles.thresholdInput}
        />
        <Button
          onClick={handleThresholdChange}
          disabled={!impactThreshold || isUpdating}
          icon={<ArrowSyncCircleRegular />}
        >
          {isUpdating ? "Updating..." : "Check different threshold"}
        </Button>
      </div>

      {!hasScores && (
        <div className={styles.centeredColumnContainerSmallGap}>
          <Text size={400}>
            No topics meet the current threshold ({currentThreshold}).
          </Text>
          <Text size={300} className={styles.fadedText}>
            Try lowering the threshold to see more topics.
          </Text>
        </div>
      )}

      {hasScores && (
        <div className={styles.chartsContainer}>
          <Plot
            data={[
              {
                y: impactAnalysisPositive.map((p) => p.x),
                x: impactAnalysisPositive.map((p) => p.y),
                text: impactAnalysisPositive.map(
                  (p) => p.name ?? ConstantValues.EMPTY_STRING,
                ),
                mode: "markers",
                type: "scatter",
                textposition: "top center",
                marker: { size: 12, color: designTokens.accent[500] },
              },
            ]}
            layout={{
              title: {
                text: `Positive Topics - threshold ${currentThreshold}`,
                font: { size: 20 },
                xanchor: "center",
              },
              hovermode: "closest",
              autosize: true,
              margin: { l: 40, r: 40, b: 40, t: 40 },
              width: 600,
              height: 500,
              xaxis: {
                title: { text: "Impact" },
                showticklabels: false,
              },
              yaxis: {
                title: { text: "Frequency" },
                showticklabels: false,
              },
            }}
            config={{ responsive: true }}
            useResizeHandler
          />

          <Plot
            data={[
              {
                y: impactAnalysisNegative.map((p) => p.x),
                x: impactAnalysisNegative.map((p) => p.y),
                text: impactAnalysisNegative.map(
                  (p) => p.name ?? ConstantValues.EMPTY_STRING,
                ),
                mode: "markers",
                type: "scatter",
                textposition: "top center",
                marker: { size: 12, color: designTokens.accent[500] },
              },
            ]}
            layout={{
              title: {
                text: `Negative Topics - threshold ${currentThreshold}`,
                font: { size: 20 },
                xanchor: "center",
              },
              hovermode: "closest",
              autosize: true,
              margin: { l: 40, r: 40, b: 40, t: 40 },
              width: 600,
              height: 500,
              xaxis: {
                title: { text: "Impact" },
                showticklabels: false,
              },
              yaxis: {
                title: { text: "Frequency" },
                showticklabels: false,
              },
            }}
            config={{ responsive: true }}
            useResizeHandler
          />

          <div className={styles.chartWrapper}>
            <Plot
              data={[
                {
                  type: "bar",
                  x: topicsPositiveChartData.map((d) => d.x),
                  y: topicsPositiveChartData.map((d) => d.y),
                  orientation: "h",
                  marker: { color: designTokens.semantic.info },
                  text: topicsPositiveChartData.map((d) => String(d.y)),
                  textposition: "none",
                  hovertemplate:
                    "<b>%{y}</b><br>Frequency: %{x}<extra></extra>",
                },
              ]}
              layout={{
                title: { text: "Positive Topics Frequency" },
                margin: { l: 250, r: 50, t: 50, b: 50 },
                width: 680,
                height: Math.max(
                  480,
                  topicsPositiveChartData.length * 30 + 100,
                ),
                xaxis: {
                  title: { text: "Frequency" },
                  automargin: true,
                },
                yaxis: {
                  title: { text: ConstantValues.EMPTY_STRING },
                  automargin: true,
                  tickmode: "array",
                  tickvals: topicsPositiveChartData.map((d) => d.y),
                  ticktext: topicsPositiveChartData.map((d) => String(d.y)),
                  tickfont: { size: 11 },
                },
                autosize: false,
                hoverlabel: {
                  bgcolor: "white",
                  font: { size: 12 },
                },
              }}
              config={{ responsive: true }}
            />
          </div>

          <div className={styles.chartWrapper}>
            <Plot
              data={[
                {
                  type: "bar",
                  x: topicsNegativeChartData.map((d) => d.x),
                  y: topicsNegativeChartData.map((d) => d.y),
                  orientation: "h",
                  marker: { color: designTokens.semantic.info },
                  text: topicsNegativeChartData.map((d) => String(d.y)),
                  textposition: "none",
                  hovertemplate:
                    "<b>%{y}</b><br>Frequency: %{x}<extra></extra>",
                },
              ]}
              layout={{
                title: { text: "Negative Topics Frequency" },
                margin: { l: 250, r: 50, t: 50, b: 50 },
                width: 680,
                height: Math.max(
                  480,
                  topicsNegativeChartData.length * 30 + 100,
                ),
                xaxis: {
                  title: { text: "Frequency" },
                  automargin: true,
                },
                yaxis: {
                  title: { text: ConstantValues.EMPTY_STRING },
                  automargin: true,
                  tickmode: "array",
                  tickvals: topicsNegativeChartData.map((d) => d.y),
                  ticktext: topicsNegativeChartData.map((d) => String(d.y)),
                  tickfont: { size: 11 },
                },
                autosize: false,
                hoverlabel: {
                  bgcolor: "white",
                  font: { size: 12 },
                },
              }}
              config={{ responsive: true }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const ATD = ({
  projectId = undefined,
  projectName = undefined,
  initialReviews: externalReviews = undefined,
  fileId = undefined,
  onTopicsChanged = undefined,
  startDownload,
  setStartDownload,
  showCards = false,
  cardTitle,
  cardHeaderAction,
  hideExtendedColumns = false,
  reviewMaxChars = undefined,
}: IATDProps): JSX.Element => {
  const styles = useDecoodaReviewsStyles();
  const dispatch = useDispatch();
  const ALL_COLUMNS = [
    "Review",
    "Topics",
    "Emotions",
    "Performance",
    "Purchase",
  ];
  const columns = hideExtendedColumns
    ? ALL_COLUMNS.filter((c) => c !== "Performance" && c !== "Purchase")
    : ALL_COLUMNS;

  const [internalReviews, setInternalReviews] = useState<IReview[]>([]);
  const [reviews, setReviews] = useState<IReview[]>([]);
  const [topics, setTopics] = useState<Record<string, ITopicInfo>>({});
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(!externalReviews);
  const [error, setError] = useState<string | null>(null);

  const [renameDialogOpen, setRenameDialogOpen] = useState<boolean>(false);
  const [renameTopicName, setRenameTopicName] = useState<string>();
  const [newTopicName, setNewTopicName] = useState<string>();
  const [isRenaming, setIsRenaming] = useState<boolean>(false);

  const [mergeDialogOpen, setMergeDialogOpen] = useState<boolean>(false);
  const [mergeTargetTopic, setMergeTargetTopic] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [mergeSourceTopics, setMergeSourceTopics] = useState<string[]>([]);
  const [mergeSearchQuery, setMergeSearchQuery] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [isMerging, setIsMerging] = useState<boolean>(false);
  const [draggedTopic, setDraggedTopic] = useState<string | null>(null);
  const [dragOverTopic, setDragOverTopic] = useState<string | null>(null);
  const [dragMergeInfo, setDragMergeInfo] = useState<IDragMergeInfo | null>(
    null,
  );
  const dialogTriggerRef = useRef<HTMLElement | null>(null);
  const topicButtonRefs = useRef<Record<string, HTMLElement | null>>({});
  const [reviewSearch, setReviewSearch] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);

  const initialReviews = externalReviews || internalReviews;

  const fetchReviews = useCallback(async (): Promise<void> => {
    if (!projectId) return;
    try {
      setIsLoading(true);
      setError(null);
      const data = await getProjectReviews({ projectId });
      setInternalReviews(data);
    } catch (err) {
      console.error("Error fetching reviews:", err);
      setError("Failed to load reviews");
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (startDownload) {
      downloadATD()
        .catch((err) => console.error("ATD download failed:", err))
        .finally(() => setStartDownload(false));
    }
  }, [startDownload, setStartDownload]);

  useEffect(() => {
    if (projectId && !externalReviews) {
      fetchReviews();
    }
  }, [fetchReviews, projectId, externalReviews]);

  useEffect(() => {
    const topicsMap: Record<string, ITopicInfo> = {};

    for (const review of initialReviews) {
      for (const t of review?.topics || []) {
        if (topicsMap[t?.text] !== undefined) {
          topicsMap[t?.text] = {
            sentiment: t?.sentiment,
            count: topicsMap[t?.text].count + 1,
          };
        } else {
          topicsMap[t?.text] = { sentiment: t?.sentiment, count: 1 };
        }
      }
    }

    const preserveOrderAndUpdateCounts = (
      currentTopics: Record<string, ITopicInfo>,
      newTopicsData: Record<string, ITopicInfo>,
    ): Record<string, ITopicInfo> => {
      const existingNames = Object.keys(currentTopics);
      if (existingNames.length === 0) {
        const sorted = Object.entries(newTopicsData).sort(
          ([, v1], [, v2]) => v2.count - v1.count,
        );
        return Object.fromEntries(sorted);
      }

      const result: Record<string, ITopicInfo> = {};
      for (const name of existingNames) {
        if (newTopicsData[name]) {
          result[name] = newTopicsData[name];
        }
      }
      const newTopics = Object.entries(newTopicsData)
        .filter(([name]) => !existingNames.includes(name))
        .sort(([, v1], [, v2]) => v2.count - v1.count);
      for (const [name, value] of newTopics) {
        result[name] = value;
      }
      return result;
    };

    setTopics(preserveOrderAndUpdateCounts(topics, topicsMap));
    setReviews(initialReviews);
  }, [initialReviews]);

  useEffect(() => {
    setCurrentPage(1);
  }, [reviewSearch, selectedTopic]);

  const filterByTopic = (topicToFilterBy: string) => (): void => {
    if (selectedTopic === topicToFilterBy) {
      setReviews(initialReviews);
      setSelectedTopic(null);
    } else {
      setReviews(
        initialReviews.filter((r) =>
          r?.topics?.map((t) => t.text)?.includes(topicToFilterBy),
        ),
      );
      setSelectedTopic(topicToFilterBy);
    }
  };

  const openRenameDialog = (topicName: string): void => {
    dialogTriggerRef.current = topicButtonRefs.current[topicName] ?? null;
    setRenameTopicName(topicName);
    setNewTopicName(topicName);
    setRenameDialogOpen(true);
  };

  const handleRename = async (): Promise<void> => {
    if (!newTopicName || newTopicName === renameTopicName) {
      setRenameDialogOpen(false);
      return;
    }

    setIsRenaming(true);
    try {
      if (projectId) {
        await renameTopicInProject({
          projectId,
          oldName: renameTopicName,
          newName: newTopicName,
        });
      } else if (fileId) {
        await renameTopic({
          fileId,
          oldName: renameTopicName,
          newName: newTopicName,
        });
      }
      setTopics((prevTopics) => {
        const updatedTopics: Record<string, ITopicInfo> = {};
        for (const [name, value] of Object.entries(prevTopics)) {
          if (name === renameTopicName) {
            updatedTopics[newTopicName] = value;
          } else {
            updatedTopics[name] = value;
          }
        }
        return updatedTopics;
      });
      setRenameDialogOpen(false);
      setRenameTopicName(ConstantValues.EMPTY_STRING);
      setNewTopicName(ConstantValues.EMPTY_STRING);
      if (onTopicsChanged) {
        onTopicsChanged();
      } else if (projectId) {
        await fetchReviews();
      }
    } catch (err) {
      console.error("Failed to rename topic:", err);
    } finally {
      setIsRenaming(false);
    }
  };

  const openMergeDialog = (topicName: string): void => {
    dialogTriggerRef.current = topicButtonRefs.current[topicName] ?? null;
    setMergeTargetTopic(topicName);
    setMergeSourceTopics([]);
    setMergeSearchQuery(ConstantValues.EMPTY_STRING);
    setMergeDialogOpen(true);
  };

  const handleDragStart = (
    e: DragEvent<HTMLButtonElement>,
    topicName: string,
  ): void => {
    setDraggedTopic(topicName);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", topicName);
  };

  const handleDragEnd = (): void => {
    setDraggedTopic(null);
    setDragOverTopic(null);
  };

  const handleDragOver = (
    e: DragEvent<HTMLButtonElement>,
    topicName: string,
  ): void => {
    e.preventDefault();
    if (draggedTopic && draggedTopic !== topicName) {
      setDragOverTopic(topicName);
    }
  };

  const handleDragLeave = (): void => {
    setDragOverTopic(null);
  };

  const handleDrop = async (
    e: DragEvent<HTMLButtonElement>,
    targetTopicName: string,
  ): Promise<void> => {
    e.preventDefault();
    const sourceTopicName = e.dataTransfer.getData("text/plain");

    setDraggedTopic(null);
    setDragOverTopic(null);

    if (sourceTopicName && sourceTopicName !== targetTopicName && !isMerging) {
      setDragMergeInfo({ source: sourceTopicName, target: targetTopicName });
      setIsMerging(true);
      try {
        if (projectId) {
          await mergeTopicsInProject({
            projectId,
            sourceTopic: sourceTopicName,
            targetTopic: targetTopicName,
          });
        } else if (fileId) {
          await mergeTopics({
            fileId,
            sourceTopic: sourceTopicName,
            targetTopic: targetTopicName,
          });
        }
        if (onTopicsChanged) {
          onTopicsChanged();
        } else if (projectId) {
          await fetchReviews();
        }
      } catch (err) {
        console.error("Failed to merge topics:", err);
        dispatch(
          showToast("Failed to merge topics. Please try again.", "error"),
        );
      } finally {
        setIsMerging(false);
        setDragMergeInfo(null);
      }
    }
  };

  const handleMerge = async (): Promise<void> => {
    if (!mergeTargetTopic || mergeSourceTopics.length === 0) {
      setMergeDialogOpen(false);
      return;
    }
    setIsMerging(true);
    try {
      for (const sourceTopic of mergeSourceTopics) {
        if (projectId) {
          await mergeTopicsInProject({
            projectId,
            sourceTopic,
            targetTopic: mergeTargetTopic,
          });
        } else if (fileId) {
          await mergeTopics({
            fileId,
            sourceTopic,
            targetTopic: mergeTargetTopic,
          });
        }
      }
      setMergeDialogOpen(false);
      setMergeSourceTopics([]);
      setMergeTargetTopic(ConstantValues.EMPTY_STRING);
      setMergeSearchQuery(ConstantValues.EMPTY_STRING);
      if (onTopicsChanged) {
        onTopicsChanged();
      } else if (projectId) {
        await fetchReviews();
      }
    } catch (err) {
      console.error("Failed to merge topics:", err);
      dispatch(showToast("Failed to merge topics. Please try again.", "error"));
    } finally {
      setIsMerging(false);
    }
  };

  const downloadATD = async () => {
    if (!initialReviews || initialReviews.length === 0) {
      dispatch(showToast("No reviews available to export.", "warning"));
      return;
    }

    const allTopics = initialReviews.flatMap((r) =>
      (r.topics || []).map((t) => ({
        name: t?.text || "",
        sentiment: t?.sentiment || "",
      })),
    );

    if (allTopics.length === 0) {
      dispatch(showToast("No topics found in reviews to export.", "warning"));
      return;
    }

    interface ITopicFrequency {
      name: string;
      sentiment: string;
      count: number;
    }

    const topicFrequencies = Object.values(
      allTopics.reduce(
        (grouped: Record<string, ITopicFrequency>, topic) => {
          const key = JSON.stringify([topic.name, topic.sentiment]);
          if (!grouped[key]) {
            grouped[key] = {
              name: topic.name,
              sentiment: topic.sentiment,
              count: 0,
            };
          }
          grouped[key].count++;
          return grouped;
        },
        {} as Record<string, ITopicFrequency>,
      ),
    ).sort((a, b) => b.count - a.count);

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([
      ["Topic Name", "Frequency", "Sentiment"],
      ...topicFrequencies.map((t) => [t.name, t.count, t.sentiment]),
    ]);

    XLSX.utils.book_append_sheet(workbook, worksheet, "ATD");

    const wbOut = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbOut], { type: "application/octet-stream" });
    saveAs(blob, formatExportFilename(projectName, "TopicResults", "xlsx"));
  };

  const getUniqueEmotions = (emotionsStr: string): string[] => {
    const allEmotions = emotionsStr
      .split("|")
      .flatMap((group) => group.split(";"))
      .map((e) => e.trim())
      .filter((e) => e !== ConstantValues.EMPTY_STRING);

    return Array.from(new Set(allEmotions));
  };

  const filteredReviews = useMemo(
    () =>
      reviewSearch.trim()
        ? reviews.filter((r) =>
            r.text?.toLowerCase().includes(reviewSearch.toLowerCase()),
          )
        : reviews,
    [reviews, reviewSearch],
  );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredReviews.length / ATD_PAGE_SIZE),
  );
  const paginatedReviews = showCards
    ? filteredReviews.slice(
        (currentPage - 1) * ATD_PAGE_SIZE,
        currentPage * ATD_PAGE_SIZE,
      )
    : filteredReviews;

  if (isLoading) {
    return (
      <div className={styles.centeredContainer}>
        <Spinner label="Loading reviews..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.centeredColumnContainer}>
        <Text>{error}</Text>
        <Button appearance="primary" onClick={fetchReviews}>
          Retry
        </Button>
      </div>
    );
  }

  if (initialReviews.length === 0) {
    return (
      <div className={styles.centeredColumnContainerSmallGap}>
        <Text size={400}>No reviews available for this project.</Text>
        <Text size={300} className={styles.fadedText}>
          Score the project to generate reviews and topic discovery data.
        </Text>
      </div>
    );
  }

  const topicsWrapperContent = (
    <div className={styles.topicsWrapper}>
      <div
        className={
          dragMergeInfo ? styles.topicsContainerDimmed : styles.topicsContainer
        }
      >
        {Object.entries(topics).map(([name, value]) => (
          <Menu key={name} openOnContext>
            <MenuTrigger disableButtonEnhancement>
              <Button
                ref={(el) => {
                  topicButtonRefs.current[name] = el;
                }}
                draggable={!isMerging}
                onDragStart={(e) => handleDragStart(e, name)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, name)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, name)}
                onClick={filterByTopic(name)}
                className={mergeClasses(
                  styles.topicButtonBase,
                  value.sentiment === "Negative"
                    ? showCards
                      ? styles.topicButtonNegativeFilled
                      : styles.topicButtonNegative
                    : showCards
                      ? styles.topicButtonPositiveFilled
                      : styles.topicButtonPositive,
                  dragOverTopic === name && styles.topicButtonDragOver,
                  selectedTopic === name && styles.topicButtonSelected,
                  draggedTopic === name && styles.topicButtonDragging,
                  isMerging
                    ? styles.topicButtonMerging
                    : styles.topicButtonGrab,
                )}
              >
                {`${name} | ${value.count}`}
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList className={styles.menuList}>
                <MenuItem
                  onClick={() => openRenameDialog(name)}
                  disabled={!projectId && !fileId}
                >
                  Rename
                </MenuItem>
                <MenuItem
                  onClick={() => openMergeDialog(name)}
                  disabled={!projectId && !fileId}
                >
                  Merge into this topic...
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        ))}
      </div>
      {dragMergeInfo && (
        <div className={styles.mergeOverlay}>
          <div className={styles.mergeOverlayContent}>
            <Spinner size="medium" />
            <span className={styles.mergeOverlayText}>
              Merging "{dragMergeInfo.source}" into "{dragMergeInfo.target}
              "...
            </span>
          </div>
        </div>
      )}
    </div>
  );

  const reviewsTableContent = (
    <Table
      aria-label="Reviews table"
      className={styles.reviewsTable}
      sortable={true}
      noNativeElements={true}
    >
      <TableHeader>
        <TableRow>
          {columns.map((column) => (
            <TableHeaderCell key={column} sortable={true}>
              {column}
            </TableHeaderCell>
          ))}
        </TableRow>
      </TableHeader>

      <TableBody>
        <VirtualizerScrollView itemSize={20} numItems={filteredReviews.length}>
          {(index) => {
            const item = filteredReviews[index];
            return (
              <TableRow key={item.id}>
                <HighlightedCell
                  text={item.text}
                  highlightRanges={item.Topic_Supporting_Phrase_Indexes}
                  maxChars={reviewMaxChars}
                />
                <TableCell>
                  <div className={styles.cellColumnLayout}>
                    {item.topics?.map((t) => (
                      <span key={t?.text}>{t?.text}</span>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <TableCellLayout>
                    <div className={styles.cellColumnLayout}>
                      {getUniqueEmotions(item.Emotions).map((emotion, idx) => (
                        <span key={idx}>{emotion}</span>
                      ))}
                    </div>
                  </TableCellLayout>
                </TableCell>
                {!hideExtendedColumns && (
                  <TableCell>{item.Performance}</TableCell>
                )}
                {!hideExtendedColumns && <TableCell>{item.Purchase}</TableCell>}
              </TableRow>
            );
          }}
        </VirtualizerScrollView>
      </TableBody>
    </Table>
  );

  const paginatedReviewsTableContent = (
    <Table
      aria-label="Reviews table"
      className={styles.reviewsTable}
      sortable={true}
      noNativeElements={true}
    >
      <TableHeader>
        <TableRow>
          {columns.map((column) => (
            <TableHeaderCell
              key={column}
              sortable={true}
              className={
                hideExtendedColumns && column === "Review"
                  ? styles.atdTableHeaderCellReviewWide
                  : styles.atdTableHeaderCell
              }
            >
              {column}
            </TableHeaderCell>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {paginatedReviews.map((item) => (
          <TableRow key={item.id} className={styles.atdTableRow}>
            <HighlightedCell
              text={item.text}
              highlightRanges={item.Topic_Supporting_Phrase_Indexes}
              highlightClass={styles.highlightedTextTeal}
              maxChars={reviewMaxChars}
              cellClassName={
                hideExtendedColumns
                  ? styles.atdTableCellReviewWide
                  : styles.atdTableCell
              }
            />
            <TableCell className={styles.atdTableCell}>
              <div className={styles.atdTableTopicsStack}>
                {item.topics?.map((t) => (
                  <span key={t?.text}>{t?.text}</span>
                ))}
              </div>
            </TableCell>
            <TableCell className={styles.atdTableCell}>
              <TableCellLayout>
                <div className={styles.cellColumnLayout}>
                  {getUniqueEmotions(item.Emotions).map((emotion, idx) => (
                    <span key={idx}>{emotion}</span>
                  ))}
                </div>
              </TableCellLayout>
            </TableCell>
            {!hideExtendedColumns && (
              <TableCell className={styles.atdTableCell}>
                <span className={styles.atdTableMonoCell}>
                  {item.Performance}
                </span>
              </TableCell>
            )}
            {!hideExtendedColumns && (
              <TableCell className={styles.atdTableCell}>
                <span className={styles.atdTableMonoCell}>{item.Purchase}</span>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <div className={styles.atdContainer}>
      {showCards ? (
        <>
          <div className={styles.atdTopicsCard}>
            {(cardTitle || cardHeaderAction) && (
              <div className={styles.atdCardHeaderRow}>
                {cardTitle && (
                  <h2 className={styles.atdCardTitle}>{cardTitle}</h2>
                )}
                {cardHeaderAction}
              </div>
            )}
            <p className={styles.atdDescription}>
              {Object.keys(topics).length} topics discovered across{" "}
              {initialReviews.length} responses. Click a topic to filter the
              reviews below; right-click to rename or merge, or drag one topic
              onto another.
            </p>
            {topicsWrapperContent}
            <p className={styles.atdFilterStatus}>
              {selectedTopic
                ? `Filtered by "${selectedTopic}"`
                : "No topic filter applied."}
            </p>
          </div>

          <div className={styles.atdReviewsCard}>
            <p className={styles.atdReviewsLabel}>REVIEWS</p>
            <div className={styles.atdSearchWrapper}>
              <SearchRegular
                className={styles.atdSearchIcon}
                aria-hidden="true"
              />
              <input
                type="search"
                className={styles.atdSearchInput}
                placeholder="Search reviews..."
                value={reviewSearch}
                onChange={(e) => setReviewSearch(e.target.value)}
                aria-label="Search reviews"
              />
            </div>
            <div className={styles.atdTableCard}>
              {paginatedReviewsTableContent}
              <div className={styles.atdTableFoot}>
                <span>
                  Showing{" "}
                  {Math.min(
                    (currentPage - 1) * ATD_PAGE_SIZE + 1,
                    filteredReviews.length,
                  )}
                  –
                  {Math.min(
                    currentPage * ATD_PAGE_SIZE,
                    filteredReviews.length,
                  )}{" "}
                  of {filteredReviews.length} reviews
                </span>
                <div className={styles.atdTableFootPagination}>
                  <button
                    className={styles.atdTableFootBtn}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    ←
                  </button>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className={styles.atdTableFootBtn}
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {topicsWrapperContent}
          {reviewsTableContent}
        </>
      )}

      <Dialog
        open={renameDialogOpen}
        onOpenChange={(_, data) => {
          if (!isRenaming) {
            setRenameDialogOpen(data.open);
            if (!data.open) {
              dialogTriggerRef.current?.focus();
              dialogTriggerRef.current = null;
            }
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Rename Topic</DialogTitle>
            <DialogContent>
              <div className={styles.dialogContent}>
                <Label htmlFor="rename-topic-input">New topic name</Label>
                <Input
                  id="rename-topic-input"
                  value={newTopicName}
                  onChange={(_, data) => setNewTopicName(data.value)}
                  placeholder="New topic name"
                  disabled={isRenaming}
                  autoFocus
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setRenameDialogOpen(false)}
                disabled={isRenaming}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleRename}
                disabled={isRenaming}
                icon={isRenaming ? <Spinner size="tiny" /> : undefined}
              >
                {isRenaming ? "Renaming..." : "Rename"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog
        open={mergeDialogOpen}
        onOpenChange={(_, data) => {
          if (!isMerging) {
            setMergeDialogOpen(data.open);
            if (!data.open) {
              dialogTriggerRef.current?.focus();
              dialogTriggerRef.current = null;
            }
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Merge Topics</DialogTitle>
            <DialogContent>
              <div className={styles.dialogContentLargeGap}>
                <div>
                  <Label className={styles.mergeIntoLabel}>Merge into: </Label>
                  <span className={styles.mergeTargetText}>
                    {mergeTargetTopic}
                  </span>
                </div>
                <div className={styles.dialogSectionSmallGap}>
                  <Label htmlFor="merge-search-input">
                    Select topics to merge
                  </Label>
                  <Input
                    id="merge-search-input"
                    placeholder="Search topics..."
                    value={mergeSearchQuery}
                    onChange={(_, data) => setMergeSearchQuery(data.value)}
                    disabled={isMerging}
                    autoFocus
                  />
                  <div className={styles.mergeTopicList}>
                    {Object.keys(topics)
                      .filter((t) => t !== mergeTargetTopic)
                      .filter((t) =>
                        t
                          .toLowerCase()
                          .includes(mergeSearchQuery.toLowerCase()),
                      )
                      .sort((a, b) => a.localeCompare(b))
                      .map((topicName) => (
                        <div key={topicName} className={styles.mergeTopicItem}>
                          <Checkbox
                            checked={mergeSourceTopics.includes(topicName)}
                            onChange={(_e, data) => {
                              if (data.checked) {
                                setMergeSourceTopics([
                                  ...mergeSourceTopics,
                                  topicName,
                                ]);
                              } else {
                                setMergeSourceTopics(
                                  mergeSourceTopics.filter(
                                    (t) => t !== topicName,
                                  ),
                                );
                              }
                            }}
                            disabled={isMerging}
                            label={topicName}
                          />
                        </div>
                      ))}
                    {Object.keys(topics)
                      .filter((t) => t !== mergeTargetTopic)
                      .filter((t) =>
                        t
                          .toLowerCase()
                          .includes(mergeSearchQuery.toLowerCase()),
                      ).length === 0 && (
                      <div className={styles.noTopicsFound}>
                        No topics found
                      </div>
                    )}
                  </div>
                </div>
                {mergeSourceTopics.length > 0 && (
                  <div className={styles.selectedCount}>
                    {mergeSourceTopics.length} topic(s) selected
                  </div>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setMergeDialogOpen(false)}
                disabled={isMerging}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleMerge}
                disabled={isMerging || mergeSourceTopics.length === 0}
                icon={isMerging ? <Spinner size="tiny" /> : undefined}
              >
                {isMerging ? "Merging..." : "Merge"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
